import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, GOOGLE_AUTH_URL } from "../lib/api";
import { useAuth } from "../hooks/useAuth";
import { GoogleIcon } from "../components/GoogleIcon";

const GOOGLE_ERROR_MESSAGES: Record<string, string> = {
  google_auth_cancelled: "Cancelaste el inicio de sesión con Google.",
  google_auth_failed: "No pudimos iniciar sesión con Google. Intenta de nuevo.",
  google_auth_invalid_state: "Tu sesión de Google expiró, intenta de nuevo.",
  google_auth_no_email: "Tu cuenta de Google no tiene un email asociado.",
};

export default function Login() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { saveSession } = useAuth();

  const googleError = searchParams.get("error");
  const initialError = googleError
    ? (GOOGLE_ERROR_MESSAGES[googleError] ?? "No pudimos iniciar sesión con Google.")
    : null;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(initialError);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState<string | null>(null);
  const [showResend, setShowResend] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setResendMsg(null);
    setShowResend(false);

    if (!email.trim() || !password) {
      setError("Ingresa tu email y contraseña.");
      return;
    }

    setLoading(true);
    const r = await api.login(email.trim(), password);
    setLoading(false);

    if (r.ok) {
      saveSession(r.data.token, r.data.user);
      navigate("/dashboard", { replace: true });
      return;
    }

    if (r.error.code === "UNVERIFIED") {
      setError("Debes verificar tu email antes de entrar.");
      setShowResend(true);
    } else {
      setError(r.error.message || "Credenciales incorrectas.");
    }
  }

  async function handleResend() {
    if (!email.trim()) return;
    setResending(true);
    setResendMsg(null);
    const r = await api.reenviarVerificacion(email.trim());
    setResending(false);
    setResendMsg(r.ok ? r.data.message : r.error.message);
  }

  return (
    <div style={{ fontFamily: "var(--font-body)", position: "relative", minHeight: "100vh", overflow: "hidden", color: "var(--ink)" }}>
      {/* blurred backdrop (landing peeking through) */}
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(900px 520px at 75% 0%, #4a4339, #33302B)", filter: "blur(2px)" }}>
        <div className="blob" style={{ position: "absolute", left: -60, top: 140, width: 320, height: 320, background: "var(--sage)", borderRadius: "60% 40% 50% 50%/55% 50% 50% 45%", opacity: 0.2, filter: "blur(20px)" }} />
        <div className="blob" style={{ position: "absolute", right: 80, bottom: -40, width: 280, height: 280, background: "#C99A6E", borderRadius: "50%", opacity: 0.2, filter: "blur(20px)", animationDelay: "-3s" }} />
        <img src="/logo-psicodosis.png" style={{ position: "absolute", right: -100, top: 60, width: 520, opacity: 0.08 }} className="hide-mobile" />
        <div className="hide-mobile" style={{ position: "absolute", left: 60, top: 200, maxWidth: 420, opacity: 0.5 }}>
          <div className="fluid-heading" style={{ fontFamily: "var(--font-display)", fontWeight: 800, lineHeight: 1.04, letterSpacing: "-0.03em", color: "var(--on-dark)" }}>
            No tienes que hacerlo solo
          </div>
        </div>
      </div>
      <div style={{ position: "absolute", inset: 0, background: "rgba(40,35,30,.35)" }} />

      {/* glass modal */}
      <div style={{ position: "relative", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
        <div className="modal-glass" style={{ maxWidth: 420, animation: "modalIn 0.4s var(--ease)" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", marginBottom: 26 }}>
            <img src="/logo-psicodosis.png" alt="PzicoDosis" style={{ width: 48, height: 48, objectFit: "contain", marginBottom: 14 }} />
            <h1 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 27, letterSpacing: "-0.02em", margin: "0 0 5px" }}>
              Bienvenido de nuevo
            </h1>
            <p style={{ fontSize: 14, color: "var(--muted)", margin: 0 }}>Inicia sesión para continuar</p>
          </div>

          {error && (
            <div className="alert alert--error" role="alert" style={{ marginBottom: 16 }}>
              ⚠ {error}
            </div>
          )}

          {showResend && (
            <div style={{ marginBottom: 16, textAlign: "center" }}>
              <button
                type="button"
                onClick={handleResend}
                disabled={resending}
                className="btn btn-outline btn--full btn--sm"
              >
                {resending ? <><span className="spinner" /> Enviando…</> : "Reenviar email de verificación"}
              </button>
              {resendMsg && (
                <p style={{ fontSize: 13, color: "var(--copper)", marginTop: 10 }}>{resendMsg}</p>
              )}
            </div>
          )}

          <a href={GOOGLE_AUTH_URL} className="btn btn-outline btn--full btn--lg" style={{ background: "rgba(255,255,255,.7)", textDecoration: "none" }}>
            <GoogleIcon /> Continuar con Google
          </a>

          <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "22px 0" }}>
            <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
            <span style={{ fontSize: 12.5, color: "var(--muted-3)", fontWeight: 600 }}>o</span>
            <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
          </div>

          <form onSubmit={handleSubmit}>
            <label className="field-label">Email</label>
            <input
              type="email" className="form-input" placeholder="tu@correo.com"
              value={email} onChange={(e) => setEmail(e.target.value)}
              style={{ marginBottom: 16 }}
            />
            <label className="field-label">Contraseña</label>
            <input
              type="password" className="form-input" placeholder="••••••••"
              value={password} onChange={(e) => setPassword(e.target.value)}
              style={{ marginBottom: 20 }}
            />
            <button type="submit" className="btn btn-primary btn--full" disabled={loading}>
              {loading ? <><span className="spinner" /> Entrando…</> : "Iniciar sesión"}
            </button>
          </form>

          <div style={{ textAlign: "center", fontSize: 14, color: "var(--muted)", marginTop: 20 }}>
            ¿No tienes cuenta?{" "}
            <Link to="/registro" style={{ color: "var(--copper)", fontWeight: 700, textDecoration: "none" }}>
              Regístrate gratis
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
